import { z } from 'zod';

// ============================================================
// Auth validation schemas
// ============================================================

export const registerSchema = z.object({
  email: z
    .string()
    .email('Invalid email address')
    .refine((e) => e.endsWith('@uci.edu'), 'Must use a @uci.edu email address'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Must contain at least one number'),
  first_name: z.string().min(1, 'First name is required').max(50),
  last_name: z.string().min(1, 'Last name is required').max(50),
  role: z.enum(['student', 'researcher']),
  invite_code: z.string().optional(),
});

export const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

export const verifyEmailSchema = z.object({
  token: z.string().min(1),
});

export const refreshTokenSchema = z.object({
  refresh_token: z.string().min(1),
});

// ============================================================
// Profile validation schemas
// ============================================================

export const studentProfileSchema = z.object({
  age: z.number().int().min(16).max(100).nullable().optional(),
  gender: z.enum(['male', 'female', 'non_binary', 'prefer_not_to_say']).nullable().optional(),
  major: z.string().max(100).nullable().optional(),
  year: z.enum(['freshman', 'sophomore', 'junior', 'senior', 'graduate', 'postdoc']).nullable().optional(),
  ethnicity: z.string().max(100).nullable().optional(),
  is_native_english_speaker: z.boolean().nullable().optional(),
  has_vision_correction: z.boolean().nullable().optional(),
  custom_tags: z.array(z.string()).optional().default([]),
});

export const researcherProfileSchema = z.object({
  department: z.string().max(200).nullable().optional(),
  lab_name: z.string().max(200).nullable().optional(),
  irb_number: z.string().max(100).nullable().optional(),
  institution: z.string().max(200).nullable().optional(),
});

// ============================================================
// Study validation schemas
// ============================================================

export const studyCriteriaSchema = z.object({
  min_age: z.number().int().min(16).max(100).optional(),
  max_age: z.number().int().min(16).max(100).optional(),
  genders: z.array(z.enum(['male', 'female', 'non_binary', 'prefer_not_to_say'])).optional(),
  majors: z.array(z.string()).optional(),
  years: z.array(z.enum(['freshman', 'sophomore', 'junior', 'senior', 'graduate', 'postdoc'])).optional(),
  ethnicities: z.array(z.string()).optional(),
  is_native_english_speaker: z.boolean().optional(),
  has_vision_correction: z.boolean().optional(),
  min_reliability_score: z.number().min(0).max(5).optional(),
  custom_tags: z.array(z.string()).optional(),
});

export const createStudySchema = z.object({
  title: z.string().min(3, 'Title must be at least 3 characters').max(200),
  description: z.string().min(10, 'Description must be at least 10 characters').max(5000),
  compensation_amount: z.number().min(0),
  compensation_type: z.enum(['cash', 'credit', 'gift_card', 'other']),
  duration_minutes: z.number().int().min(5).max(480),
  location: z.string().max(500).nullable().optional(),
  is_remote: z.boolean().default(false),
  min_reliability_score: z.number().min(0).max(5).default(0),
  max_participants: z.number().int().min(1).max(1000),
  criteria: studyCriteriaSchema.optional().default({}),
  category: z.string().max(100).nullable().optional(),
  starts_at: z.string().datetime(),
  ends_at: z.string().datetime(),
});

export const updateStudySchema = createStudySchema.partial().extend({
  status: z.enum(['draft', 'active', 'paused', 'completed', 'cancelled']).optional(),
});

// ============================================================
// Study slot validation
// ============================================================

export const createSlotSchema = z.object({
  start_time: z.string().datetime(),
  end_time: z.string().datetime(),
  capacity: z.number().int().min(1).max(100).default(1),
});

export const createSlotsSchema = z.object({
  slots: z.array(createSlotSchema).min(1).max(50),
});

// ============================================================
// Invite code validation
// ============================================================

export const createInviteCodeSchema = z.object({
  max_uses: z.number().int().min(1).max(1000).default(50),
  expires_at: z.string().datetime().optional(),
});

// ============================================================
// Inferred types
// ============================================================

export type RegisterInput = z.infer<typeof registerSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type StudentProfileInput = z.infer<typeof studentProfileSchema>;
export type ResearcherProfileInput = z.infer<typeof researcherProfileSchema>;
export type CreateStudyInput = z.infer<typeof createStudySchema>;
export type UpdateStudyInput = z.infer<typeof updateStudySchema>;
export type StudyCriteriaInput = z.infer<typeof studyCriteriaSchema>;
export type CreateSlotInput = z.infer<typeof createSlotSchema>;
export type CreateSlotsInput = z.infer<typeof createSlotsSchema>;
export type CreateInviteCodeInput = z.infer<typeof createInviteCodeSchema>;
